import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Grid,
  Stack,
  useTheme,
  useMediaQuery,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import dayjs, { Dayjs } from "dayjs";
import { getCompanyById } from "../../api/CompanyAPI";
import { getCourtsByCompany } from "../../api/CourtAPI";
import { getAvailableSchedulesByCourtAndDate } from "../../api/ReservationsAPI";
import { getCompanyData } from "../../utils/generalUtil";
import { ReservationTypes, CompanyType } from "../../types/generalTypes";
import Logo from "../../images/minimalistWhiteLogo.svg";

interface CreativeGeneratorModalProps {
  open: boolean;
  onClose: () => void;
  date?: Dayjs;
}

interface CreativeSlot {
  id: string;
  time: string;
  courtName: string;
}

const CreativeGeneratorModal: React.FC<CreativeGeneratorModalProps> = ({
  open,
  onClose,
  date = dayjs(),
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const storyRef = useRef<HTMLDivElement>(null);

  const [headline, setHeadline] = useState<string>("HORÁRIOS VAGOS");
  const [subHeadline, setSubHeadline] = useState<string>(
    "Garanta sua quadra hoje!",
  );
  const [bgColor, setBgColor] = useState<string>("#1A5ACB");
  const [textColor, setTextColor] = useState<string>("#FFFFFF");
  const [btnColor, setBtnColor] = useState<string>("#FFFFFF");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<CreativeSlot[]>([]);
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoadingData(true);
      setAvailableSlots([]);
      setSelectedSlotIds([]);

      try {
        const sessionData = getCompanyData();
        const companyId = sessionData?.companyId;

        if (!companyId) return;

        const companyInfo = (await getCompanyById(
          companyId.toString(),
        )) as CompanyType;

        if (companyInfo) {
          if (companyInfo.logoUrl) {
            setCompanyLogo(companyInfo.logoUrl);
          }
          if (companyInfo.primaryColor) {
            setBgColor(companyInfo.primaryColor);
          }
          if (companyInfo.secondaryColor) {
            setBtnColor(companyInfo.secondaryColor);
          }
        }

        const courts = await getCourtsByCompany(companyId);

        const formattedDate = date.format("YYYY-MM-DD");
        const allSlots: CreativeSlot[] = [];

        await Promise.all(
          courts.map(async (court) => {
            const slots: ReservationTypes[] =
              await getAvailableSchedulesByCourtAndDate(
                formattedDate,
                court.courtId,
                60,
              );

            if (Array.isArray(slots)) {
              slots.forEach((slot) => {
                const timeString = slot.start || "00:00";
                const timeEndString = slot.end || "00:00";

                const timeFormatted = timeString.substring(0, 5);
                const timeEnd = timeEndString.substring(0, 5);

                allSlots.push({
                  id: `${court.courtId}-${timeFormatted}`,
                  time: `${timeFormatted} - ${timeEnd}`,
                  courtName: court.name,
                });
              });
            }
          }),
        );

        allSlots.sort((a, b) => a.time.localeCompare(b.time));

        setAvailableSlots(allSlots);
        setSelectedSlotIds(allSlots.slice(0, 4).map((s) => s.id));
      } catch (error) {
        console.error("Erro ao carregar dados para o criativo:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [open]);

  const handleToggleSlot = (id: string) => {
    setSelectedSlotIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        if (prev.length >= 4) return prev;
        return [...prev, id];
      }
    });
  };

  const handleDownload = async () => {
    if (!storyRef.current) return;
    setIsGenerating(true);

    try {
      const canvas = await html2canvas(storyRef.current, {
        scale: 2,
        backgroundColor: null,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });

      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `story-vagas-${date.format("DD-MM")}.png`;
      link.click();
    } catch (error) {
      console.error("Erro ao gerar imagem:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const slotsToRender = availableSlots.filter((slot) =>
    selectedSlotIds.includes(slot.id),
  );

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Bebas+Neue&display=swap');

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes floatAnimation {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }

          @keyframes rotateGradient {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          .creative-slot-card {
            animation: fadeInUp 0.5s ease-out forwards;
            opacity: 0;
          }

          .creative-slot-card:nth-child(1) { animation-delay: 0.1s; }
          .creative-slot-card:nth-child(2) { animation-delay: 0.2s; }
          .creative-slot-card:nth-child(3) { animation-delay: 0.3s; }
          .creative-slot-card:nth-child(4) { animation-delay: 0.4s; }
        `}
      </style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            height: isMobile ? "100%" : "auto",
            maxHeight: "95vh",
            m: 1,
            overflow: "hidden",
            background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          sx={{
            background: "linear-gradient(90deg, #226FE2 0%, #0f489d 100%)",
            color: "#fff",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: "1.75rem",
                letterSpacing: "0.5px",
              }}
            >
              GERADOR DE STORIES
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                opacity: 0.9,
                fontSize: "0.75rem",
              }}
            >
              Crie seu story em tempo real
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, overflowY: { xs: "auto", md: "hidden" } }}>
          <Grid container sx={{ height: { xs: "auto", md: "100%" } }}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                p: 3,
                borderRight: { md: "1px solid rgba(0,0,0,0.08)" },
                bgcolor: "#fff",
                overflowY: "auto",
                height: { xs: "auto", md: "100%" },
                maxHeight: { xs: "none", md: "80vh" },
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      color: "#000",
                      letterSpacing: "1.5px",
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    CONTEÚDO
                  </Typography>
                  <TextField
                    fullWidth
                    label="Título"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    size="small"
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "'DM Sans', sans-serif",
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                        "&:hover fieldset": { borderColor: "#226FE2" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#226FE2",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#226FE2" },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Subtítulo"
                    value={subHeadline}
                    onChange={(e) => setSubHeadline(e.target.value)}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        fontFamily: "'DM Sans', sans-serif",
                        bgcolor: "#f8f9fa",
                        borderRadius: "8px",
                        "&:hover fieldset": { borderColor: "#226FE2" },
                        "&.Mui-focused fieldset": {
                          borderColor: "#226FE2",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#226FE2" },
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      color: "#000",
                      letterSpacing: "1.5px",
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    HORÁRIOS ({date.format("DD/MM")})
                  </Typography>
                  {loadingData ? (
                    <Box display="flex" justifyContent="center" p={3}>
                      <CircularProgress size={24} sx={{ color: "#226FE2" }} />
                    </Box>
                  ) : availableSlots.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      Nenhum horário disponível
                    </Typography>
                  ) : (
                    <List
                      dense
                      sx={{
                        width: "100%",
                        bgcolor: "#f8f9fa",
                        maxHeight: 180,
                        overflow: "auto",
                        borderRadius: "8px",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      {availableSlots.map((slot) => {
                        const labelId = `checkbox-list-label-${slot.id}`;
                        const isSelected = selectedSlotIds.includes(slot.id);
                        const isLimitReached = selectedSlotIds.length >= 4;
                        const isDisabled = !isSelected && isLimitReached;

                        return (
                          <ListItem key={slot.id} disablePadding divider>
                            <ListItemButton
                              role={undefined}
                              onClick={() =>
                                !isDisabled && handleToggleSlot(slot.id)
                              }
                              dense
                              disabled={isDisabled}
                              sx={{
                                py: 1,
                                px: 1.5,
                                opacity: isDisabled ? 0.4 : 1,
                                "&:hover": { bgcolor: "rgba(34,111,226,0.04)" },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                  inputProps={{ "aria-labelledby": labelId }}
                                  size="small"
                                  sx={{
                                    color: "rgba(0,0,0,0.3)",
                                    "&.Mui-checked": { color: "#226FE2" },
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                id={labelId}
                                primary={`${slot.time} - ${slot.courtName}`}
                                primaryTypographyProps={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      display: "block",
                      mt: 1,
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    Selecione até 4 horários
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="overline"
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      color: "#000",
                      letterSpacing: "1.5px",
                      mb: 1.5,
                      display: "block",
                    }}
                  >
                    CORES
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    {[
                      {
                        label: "Fundo",
                        value: bgColor,
                        setter: setBgColor,
                      },
                      {
                        label: "Texto",
                        value: textColor,
                        setter: setTextColor,
                      },
                      { label: "Botão", value: btnColor, setter: setBtnColor },
                    ].map(({ label, value, setter }) => (
                      <Box key={label} sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            display: "block",
                            mb: 0.5,
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: "48px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            border: "2px solid rgba(0,0,0,0.1)",
                            cursor: "pointer",
                            transition: "transform 0.2s",
                            "&:hover": { transform: "scale(1.05)" },
                          }}
                        >
                          <input
                            type="color"
                            value={value}
                            onChange={(e) => setter(e.target.value)}
                            style={{
                              position: "absolute",
                              width: "100%",
                              height: "100%",
                              border: "none",
                              cursor: "pointer",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={isGenerating}
                  sx={{
                    bgcolor: "#226FE2",
                    color: "#fff",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    py: 1.5,
                    borderRadius: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    "&:hover": {
                      bgcolor: "#0f489d",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 16px rgba(34,111,226,0.3)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {isGenerating ? "Gerando..." : "Baixar Story"}
                </Button>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={8}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: { xs: 3, md: 4 },
                minHeight: "400px",
                maxHeight: { xs: "none", md: "80vh" },
                overflowY: "auto",
                overflowX: "hidden",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  opacity: 0.3,
                },
              }}
            >
              <Box
                ref={storyRef}
                sx={{
                  width: "375px",
                  height: "667px",
                  flexShrink: 0,
                  bgcolor: bgColor,
                  color: textColor,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 4,
                  my: 2,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  fontFamily: "'DM Sans', sans-serif",
                  zIndex: 1,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                               radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)`,
                    pointerEvents: "none",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E")`,
                    pointerEvents: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    height: "70px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {companyLogo && (
                    <img
                      src={companyLogo}
                      alt="Logo"
                      style={{
                        maxHeight: "100%",
                        maxWidth: "160px",
                        objectFit: "contain",
                        filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
                      }}
                      crossOrigin="anonymous"
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: "2.5rem",
                      fontWeight: 400,
                      letterSpacing: "2px",
                      lineHeight: 0.9,
                      mb: 1.5,
                      textTransform: "uppercase",
                      textShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {headline}
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(8px)",
                      px: 2,
                      py: 0.5,
                      borderRadius: "20px",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {date.format("DD/MM/YYYY")}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    overflow: "hidden",
                    position: "relative",
                    zIndex: 2,
                    mb: 2,
                  }}
                >
                  {slotsToRender.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        opacity: 0.6,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: "0.95rem",
                          fontStyle: "italic",
                        }}
                      >
                        Selecione horários ao lado
                      </Typography>
                    </Box>
                  ) : (
                    slotsToRender.map((slot, index) => (
                      <Box
                        key={slot.id}
                        className="creative-slot-card"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.15)",
                          backdropFilter: "blur(12px)",
                          px: 2.5,
                          py: 2,
                          borderRadius: "12px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          border: "1.5px solid rgba(255,255,255,0.25)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          transition: "transform 0.3s ease",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "4px",
                            height: "100%",
                            bgcolor: "rgba(255,255,255,0.4)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'Bebas Neue', sans-serif",
                            fontSize: "1.6rem",
                            fontWeight: 400,
                            letterSpacing: "1px",
                          }}
                        >
                          {slot.time}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            opacity: 0.9,
                            textAlign: "right",
                          }}
                          noWrap
                        >
                          {slot.courtName}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>

                <Box
                  sx={{
                    mt: "auto",
                    width: "100%",
                    textAlign: "center",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "1rem",
                      fontWeight: 600,
                      mb: 2,
                      opacity: 0.95,
                    }}
                  >
                    {subHeadline}
                  </Typography>

                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: btnColor,
                      color: bgColor,
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                      border: `2px solid ${btnColor}`,
                    }}
                  >
                    RESERVAR AGORA
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      mt: 3,
                      pt: 2,
                      borderTop: "1px solid rgba(255,255,255,0.2)",
                      opacity: 0.7,
                    }}
                  >
                    <img
                      src={Logo}
                      alt="Anota"
                      style={{
                        height: "18px",
                        filter: "brightness(0) invert(1)",
                        opacity: 0.9,
                      }}
                      crossOrigin="anonymous"
                    />
                    <Typography
                      sx={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                      }}
                    >
                      Anota Reservas
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreativeGeneratorModal;
